<template>
  <div class="submission-consent" dir="rtl" @click.stop>
    <button
      class="button is-small is-light"
      :class="{ 'is-loading': loading }"
      :disabled="!applicantId"
      data-test="consent-button"
      @click.stop="toggle"
    >אישור הגשה?</button>
    <span v-if="summaryLine && !open" class="is-size-7 has-text-grey mr-2" data-test="consent-summary-line">{{ summaryLine }}</span>
    <div v-if="open && !loading" class="is-size-7 mt-1 submission-consent-summary" data-test="consent-summary">
      <p v-if="errorText" class="has-text-danger">{{ errorText }}</p>
      <template v-else-if="lines">
        <p data-test="consent-line-application">{{ lines.application }}</p>
        <p data-test="consent-line-permission">{{ lines.permission }}</p>
        <p v-if="lines.scope" data-test="consent-line-scope">{{ lines.scope }}</p>
        <p v-if="lines.restrict" class="has-text-danger has-text-weight-bold" data-test="consent-line-restrict">{{ lines.restrict }}</p>
        <p v-if="lines.note" class="has-text-grey" data-test="consent-line-note">{{ lines.note }}</p>
      </template>
    </div>
  </div>
</template>

<script>
import { formatDate } from '@/custom/helpers/format_date'

// R1.3 - read-only "did the candidate agree to this submission?" evidence.
// Click to load and show inline (no hover, no floating UI); click again to hide.
const APPLICATION_STATE = { applied: 'יש', none: 'אין תיעוד', unknown: 'לא ידוע' }
const APPLICATION_SOURCE = {
  self_application: 'הגשה עצמית',
  chat_confirmation: 'אישור בצ\'אט',
  job_board: 'לוח דרושים',
  none: ''
}
const PERMISSION_STATE = { granted: 'בתוקף', expired: 'פג תוקף', restricted: 'מוגבלת', unknown: 'לא ידוע', none: 'אין' }
// VA2 - permission scope (R2a contract). "title" lists the job titles of
// structured_scopes; a firm restriction lists allowed_company_ids (ids only).
const SCOPE = { general: 'כללי', position: 'למשרה', firm: 'למשרד', title: 'לתפקידים', unstructured: 'הערה חופשית' }

function titleIds (perm) {
  const ids = []
  ;(Array.isArray(perm.structured_scopes) ? perm.structured_scopes : []).forEach(sc => {
    (sc && Array.isArray(sc.job_title_ids) ? sc.job_title_ids : []).forEach(id => {
      if (!ids.some(x => String(x) === String(id))) ids.push(id)
    })
  })
  return ids
}

function isRestricted (perm) {
  return perm.state === 'restricted' || perm.source === 'firm_restriction'
}

export default {
  name: 'SubmissionConsentButton',
  props: {
    applicantId: { type: Number, default: null },
    offerId: { type: Number, default: null },
    positionId: { type: Number, default: null },
    // VA2 - the offer's company (companies.id), to tell whether a firm
    // restriction allows it.
    companyId: { type: Number, default: null },
    // Optional cheap summary from applicants#show (submission_consent_summary:
    // {application_state, permission_state, expires_at}); shown before any click.
    // Feed rows send a ready Hebrew string (submission_consent_summary) or null.
    summary: { type: [Object, String], default: null },
    // Feed/bubble rows: when there is no summary, say "לא ידוע" (never imply approved).
    showUnknown: { type: Boolean, default: false }
  },
  data () {
    return { open: false, loading: false, data: null, errorText: '' }
  },
  computed: {
    unknownLine () {
      return this.showUnknown ? 'אישור הגשה: לא ידוע' : ''
    },
    summaryLine () {
      const s = this.summary
      if (typeof s === 'string') return s.trim() ? `אישור הגשה: ${s.trim()}` : this.unknownLine
      if (!s) return this.unknownLine
      const app = APPLICATION_STATE[s.application_state] || s.application_state || 'לא ידוע'
      let perm = PERMISSION_STATE[s.permission_state] || s.permission_state || 'לא ידוע'
      if (this.fmt(s.expires_at)) perm += ` · עד ${this.fmt(s.expires_at)}`
      return `הגשה: ${app} · הרשאת הגשה: ${perm}`
    },
    lines () {
      if (!this.data) return null
      const app = this.data.application || {}
      const perm = this.data.submission_permission || {}
      const appParts = [APPLICATION_STATE[app.state] || app.state || 'לא ידוע']
      if (APPLICATION_SOURCE[app.source]) appParts.push(APPLICATION_SOURCE[app.source])
      if (this.fmt(app.occurred_at)) appParts.push(this.fmt(app.occurred_at))
      const permParts = [PERMISSION_STATE[perm.state] || perm.state || 'לא ידוע']
      if (this.fmt(perm.expires_at)) permParts.push(`עד ${this.fmt(perm.expires_at)}`)
      return {
        application: `הגשה: ${appParts.join(' · ')}`,
        permission: `הרשאת הגשה: ${permParts.join(' · ')}`,
        note: perm.note ? `הערה ישנה: ${perm.note}` : '',
        scope: this.scopeLine(perm),
        restrict: this.restrictLine(perm)
      }
    }
  },
  methods: {
    jobTitleName (id) {
      const store = this.$store
      const list = (store && store.getters && store.getters['jobTitles/allJobTitles']) || []
      const t = (Array.isArray(list) ? list : []).find(j => j && String(j.id) === String(id))
      return t && t.name ? t.name : `#${id}`
    },
    scopeLine (perm) {
      const label = SCOPE[perm.scope]
      if (!label) return ''
      if (perm.scope === 'title') {
        const names = titleIds(perm).map(id => this.jobTitleName(id))
        return `היקף: ${label}${names.length ? ': ' + names.join(', ') : ''}`
      }
      return `היקף: ${label}`
    },
    restrictLine (perm) {
      if (!isRestricted(perm)) return ''
      const ids = Array.isArray(perm.allowed_company_ids) ? perm.allowed_company_ids : []
      if (!ids.length) return 'המועמד ביקש להגיש רק למשרדים מסוימים'
      let line = `המועמד ביקש להגיש רק למשרד/ים: ${ids.join(', ')}`
      if (this.companyId) {
        line += ids.some(id => String(id) === String(this.companyId)) ? ' · המשרד הזה מאושר' : ' · המשרד הזה לא ברשימה'
      }
      return line
    },
    fmt (iso) {
      return formatDate(iso)
    },
    async toggle () {
      if (this.open) {
        this.open = false
        return
      }
      this.open = true
      this.loading = true
      this.errorText = ''
      try {
        this.data = await this.$network_utils.getSubmissionConsent({
          applicant_id: this.applicantId,
          offer_id: this.offerId,
          position_id: this.positionId
        })
      } catch (e) {
        this.data = null
        this.errorText = e && e.response && e.response.status === 404
          ? 'לא נמצא'
          : 'לא הצלחנו לבדוק - נסו שוב'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.submission-consent { display: inline-block; vertical-align: top; }
.submission-consent-summary p { margin: 0; line-height: 1.4; }
</style>
